import React, { ReactNode, useState, useRef, useEffect, useCallback, useMemo } from 'react'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import { calculateOptimalPosition, ElementPosition } from 'app/app/common/common-utils'

interface TooltipProps {
  id: string
  className?: string
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto'
  content: ReactNode
  children: ReactNode
  isDisabled?: boolean
}

const PADDING = 10
const DEBOUNCE_DELAY = 500

const Tooltip: React.FC<TooltipProps> = ({
  id,
  className = '',
  position = 'auto',
  content,
  children,
  isDisabled = false
}) => {
  const [visible, setVisible] = useState(false)
  const [calculatedPosition, setCalculatedPosition] = useState<ElementPosition>(position === 'auto' ? 'top' : position)
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const finalPosition = useMemo(
    () => (position === 'auto' ? calculatedPosition : position),
    [position, calculatedPosition]
  )

  const calculatePosition = useCallback(() => {
    // Early return if position is fixed or refs are not available
    if (position !== 'auto' || !containerRef.current || !tooltipRef.current) {
      return
    }

    const newPosition = calculateOptimalPosition(containerRef.current, tooltipRef.current, PADDING)
    setCalculatedPosition((prev: ElementPosition) => (prev !== newPosition ? newPosition : prev))
  }, [position])

  const debouncedCalculatePosition = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(calculatePosition, DEBOUNCE_DELAY)
  }, [calculatePosition])

  const handleShow = useCallback(() => {
    if (!isDisabled) {
      setVisible(true)
    }
  }, [isDisabled])

  const handleHide = useCallback(() => {
    if (!isDisabled) {
      setVisible(false)
    }
  }, [isDisabled])

  useEffect(() => {
    if (!visible || position !== 'auto') {
      return
    }

    // Calculate initial position
    calculatePosition()

    window.addEventListener('resize', debouncedCalculatePosition)
    window.addEventListener('scroll', debouncedCalculatePosition, true)

    return () => {
      window.removeEventListener('resize', debouncedCalculatePosition)
      window.removeEventListener('scroll', debouncedCalculatePosition, true)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [visible, position, calculatePosition, debouncedCalculatePosition])

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onMouseEnter={handleShow}
      onMouseLeave={handleHide}
      onFocus={handleShow}
      onBlur={handleHide}
      aria-describedby={id}
      tabIndex={0}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          id={id}
          role="tooltip"
          className={`tooltip tooltip--${finalPosition}`}
          style={{ pointerEvents: 'none' }}
        >
          <span className="tooltip__arrow" />
          <Text as="span">{content}</Text>
        </div>
      )}
    </div>
  )
}

export default Tooltip
